// shared preload queue: limited concurrency, real completion counts, and a retry list for assets that exhausted their automatic attempts.
export type PreloadFailure = {
    label: string
    message: string
}

export type PreloadSnapshot = {
    started: boolean
    ready: boolean
    loaded: number
    total: number
    failed: number
    failures: readonly PreloadFailure[]
    progress: number
}

export type PreloadJob = {
    // identifies the asset in the snapshot and in the log, usually its url.
    label: string
    run: () => Promise<void>
}

export type PreloadQueue = {
    // idempotent, later calls are ignored.
    start: (jobs: PreloadJob[]) => void
    retryFailed: () => void
    getSnapshot: () => PreloadSnapshot
    subscribe: (listener: () => void) => () => void
}

export type Deferred<T> = {
    promise: Promise<T>
    resolve: (value: T) => void
}

export function createDeferred<T>(): Deferred<T> {
    let resolve!: (value: T) => void
    const promise = new Promise<T>((settle) => {
        resolve = settle
    })
    return { promise, resolve }
}

export function createPreloadQueue({
    name,
    total,
    concurrency,
}: {
    name: string
    total: number
    concurrency: number
}): PreloadQueue {
    const listeners = new Set<() => void>()
    const loadedLabels = new Set<string>()
    const failedJobs = new Map<string, PreloadJob>()
    const failureMessages = new Map<string, string>()

    let started = false
    let snapshot: PreloadSnapshot = {
        started: false,
        ready: total === 0,
        loaded: 0,
        total,
        failed: 0,
        failures: [],
        progress: total === 0 ? 100 : 0,
    }

    const emit = () => {
        const loaded = loadedLabels.size

        snapshot = {
            started,
            ready: loaded >= total,
            loaded,
            total,
            failed: failedJobs.size,
            failures: [...failedJobs.keys()].map((label) => ({
                label,
                message: failureMessages.get(label) ?? 'unknown error',
            })),
            progress: total === 0 ? 100 : (loaded / total) * 100,
        }

        for (const listener of listeners) {
            listener()
        }
    }

    const run = async (jobs: PreloadJob[]) => {
        let nextJobIndex = 0

        const worker = async () => {
            while (nextJobIndex < jobs.length) {
                const job = jobs[nextJobIndex]
                nextJobIndex += 1

                try {
                    await job.run()
                    loadedLabels.add(job.label)
                    failedJobs.delete(job.label)
                    failureMessages.delete(job.label)
                } catch (error) {
                    const message = error instanceof Error ? error.message : String(error)
                    failedJobs.set(job.label, job)
                    failureMessages.set(job.label, message)
                    console.error(`[${name}] gave up on ${job.label}: ${message}`)
                }

                emit()
            }
        }

        await Promise.all(
            Array.from({ length: Math.min(concurrency, jobs.length) }, worker),
        )
    }

    return {
        start: (jobs) => {
            if (started) return
            started = true
            emit()
            void run(jobs)
        },
        retryFailed: () => {
            if (failedJobs.size === 0) return
            const jobs = [...failedJobs.values()]
            failedJobs.clear()
            failureMessages.clear()
            emit()
            void run(jobs)
        },
        getSnapshot: () => snapshot,
        subscribe: (listener) => {
            listeners.add(listener)
            return () => {
                listeners.delete(listener)
            }
        },
    }
}
